/**
 * LanguageSwitcher — sélecteur de langue dynamique.
 * Lit les langues actives depuis la table `languages` (Backend),
 * enregistre le choix dans localStorage, et recharge i18n en conséquence.
 * Peut être utilisé partout : navbars, footers, dashboards.
 */
import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import i18n from '@/i18n';
import { loadDbTranslations } from '@/lib/i18nDb';

interface Language {
  code: string;
  label: string;
  native_label: string;
  flag: string;
  dir: string;
}

const STORAGE_KEY = 'zifek_lang';

// Simple local cache so we don't re-fetch on every render
let _cachedLanguages: Language[] | null = null;

export function useLanguages() {
  const [languages, setLanguages] = useState<Language[]>(_cachedLanguages || []);
  const [loading, setLoading] = useState(!_cachedLanguages);

  useEffect(() => {
    if (_cachedLanguages) { setLanguages(_cachedLanguages); setLoading(false); return; }
    supabase
      .from('languages')
      .select('code, label, native_label, flag, dir')
      .eq('active', 1)
      .order('sort_order')
      .then(({ data }) => {
        const langs = (data as Language[]) || [];
        _cachedLanguages = langs.length ? langs : [{ code: 'fr', label: 'Français', native_label: 'Français', flag: '🇫🇷', dir: 'ltr' }];
        setLanguages(_cachedLanguages);
        setLoading(false);
      })
      .catch(() => {
        _cachedLanguages = [{ code: 'fr', label: 'Français', native_label: 'Français', flag: '🇫🇷', dir: 'ltr' }];
        setLanguages(_cachedLanguages);
        setLoading(false);
      });
  }, []);

  return { languages, loading };
}

/** Returns current language code (from localStorage or i18n). */
export function getCurrentLang(): string {
  return localStorage.getItem(STORAGE_KEY) || i18n.language || 'fr';
}

/** Sets the app language: updates i18n, localStorage, and <html dir>. */
export function setAppLanguage(code: string, dir: string = 'ltr') {
  localStorage.setItem(STORAGE_KEY, code);
  // Charge les traductions gérées en base AVANT d'appliquer la langue,
  // sinon les libellés resteraient en français par défaut.
  loadDbTranslations(code).finally(() => {
    i18n.changeLanguage(code).catch(() => {});
  });
  document.documentElement.setAttribute('lang', code);
  document.documentElement.setAttribute('dir', dir);
}

interface Props {
  compact?: boolean;
  dark?: boolean;
  className?: string;
}

export default function LanguageSwitcher({ compact = false, dark = false, className = '' }: Props) {
  const { languages, loading } = useLanguages();
  const [currentLang, setCurrentLang] = useState(getCurrentLang());
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Initialize direction from stored lang
    const lang = languages.find((l) => l.code === currentLang);
    if (lang) setAppLanguage(currentLang, lang.dir);
  }, [languages, currentLang]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (loading || languages.length <= 1) return null;

  const current = languages.find((l) => l.code === currentLang) || languages[0];

  const handleSelect = (lang: Language) => {
    setCurrentLang(lang.code);
    setAppLanguage(lang.code, lang.dir);
    setOpen(false);
    // Force a soft reload so tenant pages apply direction / font changes
    window.dispatchEvent(new CustomEvent('lang-changed', { detail: { lang: lang.code, dir: lang.dir } }));
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
          dark
            ? 'text-white/70 hover:text-white hover:bg-white/10'
            : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100 border border-background-200/70'
        }`}
        title="Changer de langue"
      >
        <span className="text-base leading-none">{current.flag}</span>
        {!compact && <span>{current.native_label}</span>}
        <i className={`ri-arrow-down-s-line text-xs transition-transform ${open ? 'rotate-180' : ''}`}></i>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 min-w-[160px] bg-background-50 border border-background-200/70 rounded-lg shadow-lg py-1 z-50">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleSelect(lang)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors cursor-pointer ${
                lang.code === currentLang
                  ? 'bg-primary-50 text-primary-700 font-semibold'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              <span className="text-base">{lang.flag}</span>
              <span>{lang.native_label}</span>
              {lang.code === currentLang && <i className="ri-check-line ml-auto text-primary-500 text-sm"></i>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}